import Helmet from '../lib/Helmet';
import { Link } from 'react-router-dom';
import { BUSINESS, PAYMENT, DELIVERY, RATES, KIT_COPPER_METRES } from '../data/pricing';
import { DISTANCE_BANDS, FREE_ABOVE, BULK_THRESHOLD, BULK_SURCHARGE } from '../data/pincodes';
import { formatINR } from '../lib/quote';

// Three policy pages, exported from one file so there is only one thing to
// place. Razorpay, PhonePe and Cashfree all check for these during KYC: a
// refund policy, terms and conditions, a shipping/delivery policy, a privacy
// policy and a contact page, each reachable from the footer. You already have
// privacy and contact.
//
// These are drafted around how Citi Cool actually trades — equipment paid
// before dispatch, installation measured and invoiced after survey, the 14-day
// stock rule Saheb set. They are not legal advice; have your CA or a lawyer
// read them before you rely on them in a dispute.
//
// Anything marked CONFIRM below is a business decision that is still open.

const UPDATED = '1 August 2026';

function Shell({ title, description, heading, children }) {
  return (
    <>
      <Helmet>
        <title>{`${title} — ${BUSINESS.name}`}</title>
        <meta name="description" content={description} />
      </Helmet>
      <main className="mx-auto w-full max-w-3xl px-4 pt-24 pb-16 lg:pt-28">
        <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">{heading}</h1>
        <p className="mt-2 text-xs text-gray-500">Last updated {UPDATED}</p>
        <div className="mt-8 space-y-7 text-sm leading-relaxed text-gray-700">{children}</div>

        <div className="mt-12 border-t border-gray-200 pt-6 text-sm text-gray-600">
          <p className="font-medium text-gray-900">Questions about this policy</p>
          <p className="mt-1">
            {BUSINESS.name}, {BUSINESS.address.street}, {BUSINESS.address.city}{' '}
            {BUSINESS.address.postalCode}
          </p>
          <p className="mt-0.5">
            <a href={`tel:${BUSINESS.phone}`} className="text-blue-700 underline">{BUSINESS.phoneDisplay}</a>
            {' · '}
            <a href={`mailto:${BUSINESS.email}`} className="text-blue-700 underline">{BUSINESS.email}</a>
          </p>
          <p className="mt-0.5">GSTIN {BUSINESS.gstin}</p>
        </div>
      </main>
    </>
  );
}

const H2 = ({ children }) => (
  <h2 className="text-base font-semibold text-gray-900">{children}</h2>
);

// ---------------------------------------------------------------------------

export function RefundPolicy() {
  return (
    <Shell
      title="Cancellation and Return Policy"
      description="Cancellation, replacement and return policy for Daikin air conditioners from Citi Cool Corporation, Delhi NCR. Replacement-only policy as per brand warranty terms."
      heading="Cancellation and return policy"
    >
      <section className="space-y-2">
        <p className="rounded-lg bg-gray-50 px-4 py-3">
          <strong className="text-gray-900">Replacement-only policy.</strong> We offer replacement,
          not refund, for products found defective or damaged within the return window, subject to
          brand warranty terms. A refund is issued only where the same model cannot be supplied.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Return window</H2>
        <p>
          Air conditioners carry a <strong>7-day</strong> return window from the date of delivery,
          for defective, damaged or wrong products only. After this window, support is provided by
          Daikin under their warranty terms.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Cancellation</H2>
        <p>
          An order may be cancelled at any time <strong>before it is dispatched</strong>. Once the
          product is out for delivery it cannot be cancelled.
        </p>
        <p>
          Cancellation requests are verified before approval. Tell us the reason on{' '}
          {BUSINESS.phoneDisplay} or {BUSINESS.email}, quoting your order reference.
        </p>
        {/* CONFIRM: Razorpay keeps roughly 2.36% (fee + GST) even when you refund
            — about ₹1,109 on a ₹47,000 order. A flat ₹500 covers you on small
            orders and leaves you short on large ones. "₹500 or 2.5%, whichever
            is higher" costs nothing extra to state and stops the leak. Drafted
            that way; change to flat ₹500 if you prefer the simpler number. */}
        <p>
          An administrative charge of <strong>{formatINR(500)} or 2.5% of the order value,
          whichever is higher</strong>, is deducted from cancellations made by the customer. This
          covers payment processing, documentation and allocation costs already incurred and is not
          recoverable by us.
        </p>
        <p>
          No charge applies where the cancellation is on our side — see below.
        </p>
      </section>

      <section className="space-y-2">
        <H2>If we cannot supply</H2>
        <p>
          If we are unable to supply the ordered unit <strong>within 14 days</strong> of confirming
          your order, you may take a full refund with no deduction, wait, or switch to another model
          with the difference settled either way.
        </p>
        <p>
          {BUSINESS.name} reserves the right to cancel any order where the product cannot be
          supplied, where a pricing or typographical error has occurred, or where the order does not
          comply with this policy. In every such case the full amount paid is refunded.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Damaged on delivery</H2>
        <p>
          Damage must be reported <strong>before the product is installed, activated or used</strong>,
          and within 48 hours of delivery. Check the carton before signing and note any damage on the
          delivery sheet.
        </p>
        <p>
          Once verified by our quality check, a replacement is issued. If inventory of the same model
          is not available, a refund is issued instead.
        </p>
        <p>
          If the product has been used or activated, the request will be rejected and no replacement
          or refund will be provided.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Defective product</H2>
        <p>
          Where a unit is found defective within the return window, Daikin inspects it at your
          premises and issues a technical report. Replacement is provided only on the basis of that
          report, and only for hardware faults.
        </p>
        <p>
          If the unit is found in non-working condition at the time of installation, our technician
          issues a job sheet stating so, and replacement follows from that.
        </p>
        <p>
          A refund is provided only where the same model is out of stock.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Wrong product</H2>
        <p>
          If the product delivered does not match what was ordered, we collect it and issue a
          replacement or a full refund after a quality check, at no cost to you.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Not eligible</H2>
        <p>The following are not eligible for return, replacement or refund:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            Change of mind, or dissatisfaction with cooling performance, noise, appearance, size or
            features reported after the product has been installed, activated or used.
          </li>
          <li>Any product installed by anyone other than an authorised technician.</li>
          <li>Damage caused after delivery, including by incorrect installation or electrical fault.</li>
          <li>Installation work already carried out.</li>
          <li>Requests raised after the 7-day return window.</li>
          <li>Products returned without the original carton, accessories, manual and warranty card.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <H2>Installation and warranty</H2>
        <p>
          To maintain warranty eligibility, the unit must be installed by an authorised technician
          within 7 days of delivery. Every installation we carry out includes nitrogen pressure
          testing and flushing at {formatINR(RATES.nitrogenTest.price)} before GST.
        </p>
        <p>
          After the return window, all support is provided by Daikin under their warranty: 1 year
          comprehensive on the unit, 10 years on the compressor and 5 years on the PCB, from the date
          of invoice.
        </p>
      </section>

      <section className="space-y-2">
        <H2>How refunds are processed</H2>
        <p>
          Where a refund is due, it is processed within 2 working days of the product being received
          back and passing quality check. Your bank or card issuer then takes a further 5 to 7
          working days depending on the payment method used.
        </p>
        <p>
          Refunds are issued to the original payment method only. Not in cash, not to a different
          account.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Unresponsive orders</H2>
        <p>
          If we cannot reach you by phone, WhatsApp or email within 8 days of your order, we reserve
          the right to cancel it and refund the amount paid, less the administrative charge above.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Raising a request</H2>
        <p>
          Call {BUSINESS.phoneDisplay} or email {BUSINESS.email} with your order reference and the reason.
          We acknowledge within one working day. All requests are subject to verification and
          quality check.
        </p>
        <p className="text-xs text-gray-500">
          {BUSINESS.name} reserves the right to amend this policy without prior notice. Nothing here
          limits your rights under the Consumer Protection Act, 2019.
        </p>
      </section>
    </Shell>
  );
}

// ---------------------------------------------------------------------------

export function TermsConditions() {
  return (
    <Shell
      title="Terms and Conditions"
      description="Terms and conditions for purchasing Daikin air conditioners and installation services from Citi Cool Corporation, authorised Daikin dealer in Delhi NCR."
      heading="Terms and conditions"
    >
      <section className="space-y-2">
        <H2>Who you are dealing with</H2>
        <p>
          This website is operated by {BUSINESS.name}, an authorised Daikin dealer trading since{' '}
          {BUSINESS.since} from {BUSINESS.address.street}, {BUSINESS.address.city}{' '}
          {BUSINESS.address.postalCode}, GSTIN {BUSINESS.gstin}.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Prices</H2>
        <p>
          Equipment prices shown include GST. Installation and material rates are shown before GST,
          which is the convention in this trade, and GST at 18% is added as a separate line on your
          invoice.
        </p>
        <p>
          Prices are those published at the time you place your order. We may change published
          prices at any time, but a confirmed order is honoured at the price confirmed. Where a
          price is displayed in obvious error, we will contact you before proceeding rather than
          either cancel silently or hold you to it.
        </p>
        <p>
          Struck-through figures are Daikin&apos;s maximum retail price for that model, shown so you
          can see the discount.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Estimates generated on this website</H2>
        <p>
          The downloadable estimate is generated automatically from list prices and the options you
          select. It is a reference document. It is not an invoice, not an official quotation, and
          not an offer to sell. Final pricing is confirmed by us in writing before any order is
          accepted.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Payment</H2>
        <p>
          Equipment: {PAYMENT.equipment.toLowerCase()}. Installation:{' '}
          {PAYMENT.installation.toLowerCase()}.
        </p>
        <p>
          You can pay for the whole order online when every unit is in stock: the equipment and delivery. Installation, if you want it, is surveyed and quoted separately; pipe runs are measured on site and billed after installation. A payment processing fee is added according to the payment method you
          choose, and is shown in the payment window before you pay.
        </p>
        <p>
          If any unit is on request, we confirm the delivery date with you first and then send a
          payment link. Online payments are handled by our payment provider; we never see or store
          your card details.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Piping kit variants</H2>
        <p>
          Daikin supplies most models in two versions. Models prefixed G ship without the piping
          kit. Models prefixed F or J ship with it and include {KIT_COPPER_METRES} metres of
          insulated copper.
        </p>
        <p>
          Choosing a without-kit unit is only sensible where existing piping can be reused. We
          inspect existing piping on site and will tell you before starting work if it has failed.
          If new copper is required it is charged at {formatINR(RATES.copperSplit.price)} per metre
          before GST.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Installation</H2>
        <p>
          Installation is optional. If you buy the unit alone, the Daikin product warranty is
          unaffected, but we accept no responsibility for the quality of an installation carried out
          by anyone else, or for faults arising from it.
        </p>
        <p>
          Every installation we carry out includes nitrogen pressure testing and flushing at{' '}
          {formatINR(RATES.nitrogenTest.price)} before GST. This is not optional on our work.
        </p>
        <p>
          Pipe runs are measured on site. Copper, drain piping and control wiring are three separate
          runs of differing lengths and are each charged per metre at the published rates.
        </p>
        <p>
          You are responsible for providing safe access, a suitable mounting position, and a working
          power point within reach. Civil work such as structural cutting, false ceiling
          modification and electrical upgrades is outside our scope unless separately agreed.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Where we operate</H2>
        <p>
          We supply and install across {BUSINESS.serviceAreas.join(', ')}. Orders outside this area
          are handled case by case; we will tell you before accepting rather than after.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Warranty</H2>
        <p>
          All products carry the manufacturer&apos;s warranty from Daikin: 1 year comprehensive on
          the unit, 10 years on the compressor and 5 years on the PCB, from the date of invoice.
          Warranty terms, exclusions and claim procedure are Daikin&apos;s. We assist with claims but
          the warranty is between you and Daikin.
        </p>
        <p>
          Our workmanship on installations we carry out is guaranteed for 12 months.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Limits</H2>
        <p>
          Our liability in connection with any order is limited to the amount you paid for it. We are
          not liable for indirect or consequential loss, including loss of use or business
          interruption.
        </p>
        <p>
          Nothing here limits any right you have under the Consumer Protection Act, 2019, or other
          applicable Indian law.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Governing law</H2>
        <p>
          These terms are governed by the laws of India. Disputes are subject to the exclusive
          jurisdiction of the courts of Delhi.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Related policies</H2>
        <p>
          See also our{' '}
          <Link to="/refund-policy" className="text-blue-700 underline">refund and cancellation policy</Link>,{' '}
          <Link to="/delivery-policy" className="text-blue-700 underline">delivery policy</Link> and{' '}
          <Link to="/privacy-policy" className="text-blue-700 underline">privacy policy</Link>.
        </p>
      </section>
    </Shell>
  );
}

// ---------------------------------------------------------------------------

export function DeliveryPolicy() {
  return (
    <Shell
      title="Delivery and Installation Policy"
      description="Delivery and installation policy for Daikin air conditioners from Citi Cool Corporation. 24-hour delivery across Delhi, Noida, Gurgaon and Faridabad."
      heading="Delivery and installation policy"
    >
      <section className="space-y-2">
        <H2>Where we deliver</H2>
        <p>
          Delhi NCR only — Delhi, Gurugram, Noida, Greater Noida, Ghaziabad and Faridabad. We
          dispatch from two points, South Extension Part 2 and Chattarpur, and your order goes from
          whichever is nearer.
        </p>
        <p>
          If your pincode is not serviceable, the cart will say so before you pay rather than after.
          Call us on {BUSINESS.phoneDisplay} if you are just outside and we will tell you honestly whether
          we can help.
        </p>
      </section>

      <section className="space-y-2">
        <H2>How quickly</H2>
        <p>
          Models marked <strong>24-hour delivery</strong> on this website are physically in our
          stock at the time you look. {DELIVERY.footnote}
        </p>
        <p>
          Stock is held per variant. The same model may be available immediately with the piping kit
          and on order without it, or the reverse, so check the badge against the version you are
          buying.
        </p>
        <p>
          Models without the badge are available to order. Lead times are typically 3 to 7 working
          days depending on Daikin&apos;s despatch, and we confirm a date before taking payment.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Delivery charges</H2>
        <p>
          Delivery is charged by road distance from whichever of our two dispatch points is
          nearer, so you never pay for a longer trip than the one we actually make. Enter your
          pincode in the cart and the exact figure appears before you pay. Nothing is added
          afterwards, and GST is already inside the number.
        </p>

        <table className="mt-3 w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 font-medium">Road distance</th>
              <th className="py-2 text-right font-medium">Within Delhi</th>
              <th className="py-2 text-right font-medium">Rest of NCR</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {DISTANCE_BANDS.map((b) => (
              <tr key={b.label}>
                <td className="py-2 text-gray-600">Up to {b.maxKm} km</td>
                <td className="py-2 text-right text-gray-900">{formatINR(b.priceInDelhi)}</td>
                <td className="py-2 text-right text-gray-900">{formatINR(b.priceOutsideDelhi)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-2 text-sm text-gray-500">
          These are the most any address in that band pays, GST included. Your pincode is priced
          on its own exact distance, so the figure in the cart is usually lower.
        </p>

        <p className="mt-2">
          Gurugram, Noida, Faridabad and Ghaziabad cost a little more than the same distance
          inside Delhi, because a vehicle dropping there comes back empty. Up to{' '}
          {BULK_THRESHOLD} units travel together as one trip; beyond that we send a second
          vehicle. Delivery is free on orders of {formatINR(FREE_ABOVE)} and above.
        </p>
      </section>

      <section className="space-y-2">
        <H2>On the day</H2>
        <p>
          Someone over 18 must be present to receive the delivery. Please check the packaging before
          signing. If the carton is damaged, note it on the delivery sheet and tell us the same day.
        </p>
        <p>
          Delivery and installation are usually separate visits. We schedule installation once the
          unit is with you and the site is ready.
        </p>
      </section>

      <section className="space-y-2">
        <H2>Installation scheduling</H2>
        <p>
          Installation is booked by phone once delivery is complete, typically within 2 to 3 working
          days depending on your availability and ours. Peak summer weeks run longer, and we will
          tell you rather than let you find out.
        </p>
        <p>
          A site survey is carried out before or at the start of installation to measure pipe runs
          and check existing piping where you have chosen a without-kit unit.
        </p>
      </section>

      <section className="space-y-2">
        <H2>If something goes wrong</H2>
        <p>
          Call {BUSINESS.phoneDisplay} or email {BUSINESS.email} with your order reference. See our{' '}
          <Link to="/refund-policy" className="text-blue-700 underline">refund and cancellation policy</Link>{' '}
          for what happens if we cannot supply within 14 days.
        </p>
      </section>
    </Shell>
  );
}

export default RefundPolicy;